type FavoriteChangeCallbackFunction = () => any | never;

const favoriteChangeCallbacks: FavoriteChangeCallbackFunction[] = [];

function executeCallbacks() {
	for (const fnCallback of favoriteChangeCallbacks) {
		fnCallback();
	}
}

/**
 * Returns an array of values under the specified name (or empty array).
 * @param name The name of the localStorage key.
 */
export function getLocalStorageItem(name: string): [any] {
	try {
		const value = localStorage.getItem(name);
		return typeof value === 'string' ? JSON.parse(value) : [];
	} catch (e) {
		// tslint:disable-next-line
		console.warn('localStorage unavailable - please enable localStorage to use this application.');
	}

	return [] as any;
}

/**
 * Sets the specified value in an array under the specified name.
 * @param name The name (key) of the localStorage item.
 * @param value The value to store in the array.
 */
export function setLocalStorageItem(name: string, value: string) {
	try {
		const itemExists = localStorage.getItem(name);
		if (itemExists) {
			const items: string[] = JSON.parse(itemExists);
			// add value only if it does not exist already
			if (items.indexOf(value) < 0) {
				items.push(value);
				localStorage.setItem(name, JSON.stringify(items));
			}
		} else {
			const arr: string[] = [];
			arr.push(value);
			localStorage.setItem(name, JSON.stringify(arr));
		}

		executeCallbacks();
	} catch (e) {
		// tslint:disable-next-line
		console.warn('localStorage unavailable - please enable localStorage to use this application.');
	}
}

/**
 * Delets the specified value out of the array under the specified name.
 * @param name The name (key) of the localStorage item.
 * @param value The value to delete in the array.
 */
export function deleteLocalStorageItem(name: string, value: string) {
	try {
		const itemExists = localStorage.getItem(name);
		if (itemExists) {
			const items: string[] = JSON.parse(itemExists);
			const index = items.indexOf(value);
			if (index >= 0) {
				items.splice(index, 1);
				localStorage.setItem(name, JSON.stringify(items));
			}

			executeCallbacks();
		}
	} catch (e) {
		// tslint:disable-next-line
		console.warn('localStorage unavailable - please enable localStorage to use this application.');
	}
}

/**
 * Returns true if the value exists in the array under the specified name.
 * @param name The name (key) of the localStorage item.
 * @param value The value to look for in the array.
 */
export function localStorageValueExists(name: string, value: string): boolean {
	try {
		const itemExists = localStorage.getItem(name);
		if (itemExists) {
			const items: string[] = JSON.parse(itemExists);
			const index = items.indexOf(value);
			return index >= 0;
		}
		return false;
	} catch (e) {
		// tslint:disable-next-line
		console.warn('localStorage unavailable - please enable localStorage to use this application.');
	}

	return false;
}

/**
 * Adds a function callback to be executed when something is getting added / removed to / from favorites
 * @param fnCallback The function to execute
 */
export function registerFavoritesChangeCallback(fnCallback: FavoriteChangeCallbackFunction) {
	favoriteChangeCallbacks.push(fnCallback);
}

/**
 * Removes a function callback from the array of functions to execute
 * @param fnCallback The function to execute
 */
export function removeFavoritesChangeCallback(fnCallback: FavoriteChangeCallbackFunction) {
	const index = favoriteChangeCallbacks.indexOf(fnCallback);

	if (index >= 0) {
		favoriteChangeCallbacks.splice(index, 1);
	}
}
